import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Envelope, Eye, EyeSlash, Lock, SignIn, SpinnerGap } from '@phosphor-icons/react';
import { internshipAPI } from '../../services/api';
import BrandLockup from '../BrandLockup';

const InternLogin = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = 'Sign in | Growmore IT Services';
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (localStorage.getItem('intern_token')) {
      navigate('/intern/portal', { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    const formData = new FormData(event.currentTarget);
    try {
      const { data } = await internshipAPI.login({
        email: String(formData.get('email') || '').trim(),
        password: String(formData.get('password') || '')
      });
      if (data?.token) {
        localStorage.setItem('intern_token', data.token);
        navigate('/intern/portal');
        return;
      }
      setError('Could not sign in.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not sign in. Check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col [&_.input-field]:!bg-white [&_.input-field]:!text-gray-900 [&_.input-field]:!border-gray-200">
      <div className="px-4 pt-[max(12px,env(safe-area-inset-top))]">
        <Link to="/" className="inline-flex items-center text-sm font-semibold text-gray-500 py-3">
          ← Website
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <form onSubmit={onSubmit} className="w-full max-w-[400px] bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex flex-col items-center mb-5">
            <BrandLockup as="div" size="sm" className="shrink-0" />
            <h1 className="mt-3 font-display text-xl font-extrabold text-gray-900">Sign in</h1>
            <p className="text-sm text-gray-500 text-center mt-1">Email and password from your offer.</p>
          </div>

          <label className="block text-sm font-semibold text-gray-800 mb-1">Email</label>
          <div className="relative mb-3">
            <Envelope className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input name="email" type="email" required autoComplete="username" className="input-field !pl-11 text-base" placeholder="Email" />
          </div>

          <label className="block text-sm font-semibold text-gray-800 mb-1">Password</label>
          <div className="relative mb-2">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              className="input-field !pl-11 !pr-11 text-base"
              placeholder="Password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2 text-gray-400"
              aria-label="Show password"
            >
              {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <p className="text-sm mb-4 text-right">
            <Link to="/intern/forgot" className="font-semibold text-brand-600">Forgot password?</Link>
          </p>

          {error && (
            <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
              {error}
            </p>
          )}

          <button type="submit" disabled={loading} className="btn-primary text-white w-full inline-flex items-center justify-center gap-2 hover:!scale-100">
            {loading ? <SpinnerGap className="w-5 h-5 animate-spin" /> : <SignIn className="w-5 h-5" weight="bold" />}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
};

export default InternLogin;
